import React from 'react';
import { Tooltip } from 'antd';
import { FD210 } from '../../../services/pwd210Api';
import { Mode } from '../hooks/usePwd210Mode';

interface Perms {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canPrint: boolean;
  canApprove: boolean;
  hasAnyPermission: boolean;
}

interface ActionBarProps {
  busy: boolean;
  mode: Mode;
  isEditing: boolean;
  record: FD210 | null;
  currentIdx: number;
  allIdsLength: number;
  permissions: Perms;
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onPrint: () => void;
  onNavTo: (idx: number) => void;
  onExit: () => void;
}

/** Permission-wrapped button */
const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, title, onClick, children }) => {
  const btn = (
    <button
      className={className || 'btn'}
      disabled={disabled || !allowed}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
  if (!allowed) {
    return <Tooltip title="權限不足">{btn}</Tooltip>;
  }
  return btn;
};

const Pwd210ActionBar: React.FC<ActionBarProps> = React.memo(({
  busy, mode, isEditing, record, currentIdx, allIdsLength,
  permissions, onAdd, onModify, onDelete, onPrint, onNavTo, onExit,
}) => {
  const { canView, canCreate, canEdit, canDelete, canPrint } = permissions;

  return (
    <div className="action-bar">
      {/* Navigation */}
      <div className="action-group" data-acceptance-action="PWD210F1-ACT-LIST">
        <PermBtn allowed={canView} disabled={busy || isEditing || currentIdx <= 0} onClick={() => onNavTo(0)} title="最前">
          ⏮ 最前
        </PermBtn>
        <PermBtn allowed={canView} disabled={busy || isEditing || currentIdx <= 0} onClick={() => onNavTo(currentIdx - 1)} title="上一筆">
          ◀ 上一筆
        </PermBtn>
        <span className="nav-info">{allIdsLength > 0 ? `${currentIdx + 1} / ${allIdsLength}` : '0 / 0'}</span>
        <PermBtn allowed={canView} disabled={busy || isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(currentIdx + 1)} title="下一筆">
          下一筆 ▶
        </PermBtn>
        <PermBtn allowed={canView} disabled={busy || isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(allIdsLength - 1)} title="最後">
          最後 ⏭
        </PermBtn>
      </div>

      <div className="action-separator" />

      {/* CRUD */}
      <div className="action-group">
        {/* 新增 ↔ 存檔 */}
        <span data-acceptance-action="PWD210F1-ACT-CREATE">
        <PermBtn
          allowed={mode === 'edit' ? canEdit : canCreate}
          disabled={busy}
          className={isEditing ? 'btn btn-primary' : 'btn'}
          onClick={onAdd}
        >
          {isEditing ? '💾 存檔' : '➕ 新增'}
        </PermBtn>
        </span>

        {/* 修改 ↔ 還原 */}
        <span data-acceptance-action="PWD210F1-ACT-UPDATE">
        <PermBtn
          allowed={canEdit}
          disabled={busy || (!record && !isEditing)}
          className={isEditing ? 'btn btn-warning' : 'btn'}
          onClick={onModify}
        >
          {isEditing ? '↩ 還原' : '✏️ 修改'}
        </PermBtn>
        </span>

        {/* 刪除 */}
        <span data-acceptance-action="PWD210F1-ACT-DELETE">
        <PermBtn allowed={canDelete} disabled={busy || !record || isEditing} onClick={onDelete}>
          🗑 刪除
        </PermBtn>
        </span>
      </div>

      <div className="action-separator" />

      {/* Print + Exit */}
      <div className="action-group">
        <PermBtn allowed={canPrint} disabled={busy || !record || isEditing} onClick={onPrint}>
          🖨 列印
        </PermBtn>
        <button className="btn" onClick={onExit}>
          離開
        </button>
      </div>
    </div>
  );
});

Pwd210ActionBar.displayName = 'Pwd210ActionBar';

export default Pwd210ActionBar;
